import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API } from '../App';
import './PatientHistoryPage.css';

export default function PatientHistoryPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/api/patients/${id}`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(setPatient)
      .catch(() => navigate('/'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <HistorySkeleton />;
  if (!patient) return null;

  const fullName = `${patient.first_name} ${patient.last_name.toUpperCase()}`;
  // Les plus récentes en premier, numérotées dans l'ordre chronologique réel
  // (consultation la plus ancienne = n°1) pour que le numéro affiché reste
  // stable même si de nouvelles consultations sont ajoutées ensuite.
  const consultations = patient.consultations || [];
  const numbered = consultations.map((c, i) => ({ ...c, number: i + 1 })).reverse();

  return (
    <div className="ph-page">
      <header className="ph-header">
        <button className="back-btn" onClick={() => navigate('/')}>{t('common.back')}</button>
        <p className="ph-eyebrow">{t('patients.history_title')}</p>
        <h1 className="ph-title">{fullName}</h1>
      </header>

      <div className="ph-list">
        <button className="ph-row ph-row-new" onClick={() => navigate(`/patient/${id}/admin`)}>
          <span className="ph-row-icon">+</span>
          <span className="ph-row-label">{t('patients.new_consultation')}</span>
          <span className="ph-row-arrow">→</span>
        </button>

        {numbered.length === 0 && (
          <p className="ph-empty">{t('patients.no_consultations')}</p>
        )}

        {numbered.map(c => (
          <button
            key={c.id}
            className="ph-row ph-row-past"
            onClick={() => navigate(`/analysis/${id}?consultation_id=${c.id}`)}
          >
            <span className="ph-row-number">{t('patients.consultation_number', { number: c.number })}</span>
            <span className="ph-row-date">
              {c.date
                ? new Date(c.date).toLocaleDateString(i18n.language, { day: '2-digit', month: 'long', year: 'numeric' })
                : '—'}
            </span>
            <span className="ph-row-arrow">→</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function HistorySkeleton() {
  return (
    <div className="ph-page">
      <div className="ph-header" style={{ marginBottom: 32 }}>
        <div className="sk sk-line" style={{ width: 80, height: 12, marginBottom: 16 }} />
        <div className="sk sk-line" style={{ width: 240, height: 32 }} />
      </div>
      <div className="ph-list">
        {[0, 1, 2].map(i => (
          <div key={i} className="sk" style={{ height: 52, borderRadius: 12, marginBottom: 10 }} />
        ))}
      </div>
    </div>
  );
}
